import * as React from 'react';
import { Alignment, MenuAlignment, MenuPosition } from './ContextualMenu.types';
import { AnimatePresence, Variants, motion } from 'framer-motion';
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react';
import { createLocators } from '@u1/locators';
import { mergeRefs } from '../../../utils/mergeRefs';
import { useOnClickOutside } from '../../../hooks/useClickOutside';

import clsx from 'clsx';

import { menuVariants } from '../../../animation';
import useStyles from './ContextualMenu.styles';

const alignmentTransformMap: Record<MenuAlignment, Alignment> = {
  left: 'start',
  right: 'end',
};

export interface IContextualMenuProps {
  /**
   * Shows / hides contextual menu
   */
  isOpen: boolean;
  /**
   * Limits maximum available height
   */
  maxHeight?: string;
  /**
   * Limits maximum available width
   */
  width?: 'narrow' | 'medium' | 'fullwidth';
  /**
   * Contextual menu position
   */
  position?: MenuPosition;
  /**
   * Contextual menu position alignment
   */
  alignment?: MenuAlignment;
  /**
   * On click outside callback
   */
  onClickOutside?: () => unknown;
  /**
   * Flip the position of the menu automatically if there is not enough space
   */
  autoPosition?: boolean;
  /**
   * Element ref
   */
  ref?: React.Ref<HTMLDivElement>;
  /**
   * Escape hatch to add styles to the checkbox wrapper
   */
  _UNSAFE_classname?: string;
  /**
   * Component that triggers contextual menu. Is used to position the contextual menu relative to this component.
   */
  triggerComponent?: React.ReactElement;
}

export const contextualMenuLocators = createLocators({
  name: 'ContextualMenu',
  componentLocators: ['menu', 'options'],
});
const { locators } = contextualMenuLocators();

const ContextualMenu: React.FC<React.PropsWithChildren<IContextualMenuProps>> =
  React.forwardRef<
    HTMLDivElement,
    React.PropsWithChildren<IContextualMenuProps>
  >(
    (
      {
        children,
        isOpen,
        width = 'fullwidth',
        maxHeight = '360px',
        position = 'bottom',
        alignment = 'left',
        onClickOutside,
        autoPosition = false,
        triggerComponent,
        _UNSAFE_classname,
      },
      ref,
    ) => {
      const triggerRef = React.useRef<HTMLDivElement>(null);
      const { refs, strategy, x, y, context } = useFloating({
        strategy: 'fixed',
        placement: `${position}-${alignmentTransformMap[alignment]}`,
        middleware: [autoPosition ? flip() : undefined, offset(4)],

        whileElementsMounted: autoUpdate,
      });

      const handleClickOutside = React.useCallback(() => {
        isOpen && onClickOutside && onClickOutside();
      }, [isOpen, onClickOutside]);

      const useClickOutsideRef = useOnClickOutside<HTMLDivElement>(() => {
        handleClickOutside();
      });

      const classes = useStyles();

      const hiddenPlacement = (context.placement as string).includes('top')
        ? 'hiddenTop'
        : 'hiddenBottom';
      return (
        <div
          ref={mergeRefs([useClickOutsideRef, triggerRef])}
          className={_UNSAFE_classname}
        >
          <div className={classes.trigger} ref={refs.setReference}>
            {triggerComponent}
          </div>
          <AnimatePresence>
            {isOpen ? (
              <motion.div
                {...locators.menu({
                  isOpen,
                  width,
                })}
                ref={mergeRefs([ref, refs.setFloating])}
                variants={menuVariants as Variants}
                initial={hiddenPlacement}
                animate={isOpen ? 'visible' : 'hidden'}
                exit={hiddenPlacement}
                style={{
                  maxHeight: maxHeight,
                  position: strategy,
                  top: y,
                  left: x,
                  width:
                    width === 'fullwidth'
                      ? triggerRef.current?.offsetWidth
                      : undefined,
                }}
                className={clsx(classes[width], {
                  [classes.listWrapper]: true,
                })}
              >
                <div {...locators.options()} className={classes.list}>
                  {children}
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      );
    },
  );

export default ContextualMenu;
